import {getSubmission, listPhotoBlobsByCsid} from './offlineQueue.js';
import {getRpcFormConfig, isRpcRequest} from './offlineRpcForms.js';
import type {RpcRequest} from './offlineRpcForms.js';
import {queuedReportActor} from './queuedReportApi.js';
import {photoDigest} from './queuedReportPhotos.js';
import type {SupabaseClient} from './supabaseTypes.js';

export type QueuedRpcRecovery = {
  csid: string;
  actorProfileId: string;
  legacyIdentity: boolean;
  payload: Record<string, unknown>;
  record: RpcRequest;
  photo: File | null;
};

const object = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);

// Open/Cancel are read-only. Follow an existing replacement rather than create
// another sibling; retain all original data when shape or bytes cannot be proved.
export async function loadQueuedRpcRecovery(
  sb: SupabaseClient,
  csid: string,
  formKind: string,
): Promise<QueuedRpcRecovery> {
  const actorProfileId = await queuedReportActor(sb);
  if (!actorProfileId) throw new Error('Sign in to open this report.');
  const seen = new Set<string>();
  let row = await getSubmission(csid);
  while (row?.replacement_csid) {
    if (seen.has(row.csid) || seen.size >= 64) throw new Error('Recovery links need help. The reports were retained.');
    seen.add(row.csid);
    row = await getSubmission(row.replacement_csid);
  }
  const cfg = getRpcFormConfig(formKind);
  if (
    !row ||
    row.form_kind !== formKind ||
    !object(row.payload) ||
    !isRpcRequest(row.record) ||
    row.record.rpc !== cfg.rpc ||
    !object(row.record.args.parent_in)
  )
    throw new Error('Open this retained report in its original form.');
  if (row.actor_profile_id && row.actor_profile_id !== actorProfileId)
    throw new Error('Sign in with the account that queued this report.');
  const blobs = await listPhotoBlobsByCsid(row.csid);
  const path = row.record.args.parent_in.request_photo_path;
  let photo: File | null = null;
  if (cfg.hasPhoto && (path || blobs.length)) {
    if (blobs.length !== 1 || (path && path !== `task-request-photos/${blobs[0].key}`)) {
      throw new Error('Queued photo bytes are missing. The report was retained.');
    }
    if (
      row.record.photo &&
      (row.record.photo.path !== path || (await photoDigest(blobs[0].blob)) !== row.record.photo.sha256)
    ) {
      throw new Error('Queued photo bytes changed. The report was retained.');
    }
    photo = new File([blobs[0].blob], blobs[0].name || 'photo-1.jpg', {type: blobs[0].mime});
  } else if (blobs.length) throw new Error('Unexpected queued photos. The report was retained.');
  if ((await queuedReportActor(sb)) !== actorProfileId)
    throw new Error('The signed-in account changed. Open the report again.');
  return {
    csid: row.csid,
    actorProfileId,
    legacyIdentity: !row.actor_profile_id,
    payload: row.payload,
    record: row.record,
    photo,
  };
}
